// postMessage protocol between the loader (host page) and the messenger iframe.
import type { TriggerBootContext, VisitorTrigger } from './triggers';
import type { Message, WidgetSettings } from './types';

export const PROTOCOL = 'chatwidget/v1';

export interface BootPayload {
    token: string;
    contact: WidgetContact;
    workspace: {
        id: string;
        name: string;
        /** Visitor-safe settings: the AI's private instructions are never sent. */
        settings: Omit<WidgetSettings, 'ai'> & { ai: { enabled: boolean; name: string } };
        /** Help center: shown as a messenger tab once articles are published. */
        help?: { enabled: boolean; url: string };
        team: { id: number; name: string; avatar_url: string | null }[];
        agents_online: boolean;
        office_hours_open: boolean | null;
    };
    realtime: { key: string; host: string; port: number; scheme: string };
    unread_count: number;
    identity_error: string | null;
    /** Dashboard appearance editor: no API/websocket calls, sample data. */
    preview?: boolean;
    /** Active co-browsing session to resume after a page navigation. */
    cobrowse?: CobrowseSessionPayload | null;
    /** Automatic messages, highest priority first. */
    triggers?: VisitorTrigger[];
    trigger_context?: TriggerBootContext;
    /** Dashboard playground: show triggers in the preview canvas, as if on this URL. */
    trigger_test?: { url: string } | null;
}

/** A trigger message the visitor clicked: the messenger opens a new chat that starts with it. */
export interface TriggerOpener {
    trigger_id: string;
    message: string;
    sender: VisitorTrigger['sender'];
    shown_at: string;
}

export interface CobrowseSessionPayload {
    id: string;
    contact_id: string;
    started_at: string;
    started_by: { id: number; name: string } | null;
}

/** Agent → visitor co-browsing signals (whispered on `cobrowse.{contactId}`). */
export type CobrowseAgentSignal =
    | { kind: 'ping'; name?: string }
    | { kind: 'snapshot' }
    | { kind: 'pointer'; x: number | null; y: number | null; name?: string }
    | { kind: 'highlight'; x: number; y: number };

/** Visitor → agent stream chunk (whispered on `cobrowse.{contactId}`). */
export interface CobrowseChunk {
    sid: string;
    seq: number;
    part: number;
    parts: number;
    /** Base64 of deflated JSON (array of rrweb events). */
    d: string;
}

export interface WidgetContact {
    id: string;
    name: string | null;
    email: string | null;
    display_name: string;
    is_identified: boolean;
}

/** Loader → iframe. */
export type ToFrame =
    | { type: 'init'; boot: BootPayload; open: boolean }
    | { type: 'open'; prefill?: string; trigger?: TriggerOpener }
    | { type: 'close' }
    | { type: 'contact'; contact: WidgetContact }
    | { type: 'page-visible'; visible: boolean }
    | { type: 'preview-settings'; settings: WidgetSettings; workspaceName?: string }
    | { type: 'cobrowse-events'; events: unknown[] }
    | { type: 'cobrowse-sync'; session: CobrowseSessionPayload | null };

/** Iframe → loader. */
export type FromFrame =
    | { type: 'ready' }
    | { type: 'request-close' }
    | { type: 'unread'; count: number }
    | { type: 'preview'; message: Message }
    | { type: 'clear-previews' }
    | { type: 'activity' }
    | { type: 'cobrowse-start' }
    | { type: 'cobrowse-stop' }
    | { type: 'cobrowse-snapshot' }
    | { type: 'cobrowse-pointer'; x: number | null; y: number | null; name?: string }
    | { type: 'cobrowse-highlight'; x: number; y: number };

export interface Envelope<T> {
    protocol: typeof PROTOCOL;
    payload: T;
}

export function envelope<T>(payload: T): Envelope<T> {
    return { protocol: PROTOCOL, payload };
}

export function isEnvelope<T>(data: unknown): data is Envelope<T> {
    return typeof data === 'object' && data !== null && (data as Envelope<T>).protocol === PROTOCOL;
}
